import type React from "react";
import { useCallback, useContext, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { type TierDetails } from "~/api/types";
import {
  ChartReorderContext,
  DocumentCard,
  DocumentLines,
} from "./DocumentCard";
import { type ChartColumn } from "../helpers/chart";
import { type DocumentVersionSummary } from "~/api/types";
import { Button } from "~/atoms/button";
import { Info } from "lucide-react";
import { CategoryModal } from "./CategoryModal";
import { DragDropProvider, type DragDropEventHandlers } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import { moveItemByIndex } from "~/helpers/moveItemByIndex";
import { updateDocumentOrder } from "~/api/v2/requests";

type OnDragEnd = NonNullable<DragDropEventHandlers["onDragEnd"]>;

interface ChartDocumentsProps {
  columns: ChartColumn[];
  tiers: TierDetails[];
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  setSelectedId: (id: number) => void;
  canManage: boolean;
  onReorder: () => void;
  category?: { id: number; title: string };
}

const ChartDocuments = ({
  columns,
  tiers,
  documentRefsMap,
  containerRef,
  setSelectedId,
  canManage,
  onReorder,
  category,
}: ChartDocumentsProps) => {
  const [reorderKey, setReorderKey] = useState(0);

  const notifyReorderStart = () => {
    setReorderKey((k) => k + 1);
  };

  return (
    <ChartReorderContext.Provider value={{ reorderKey, notifyReorderStart }}>
      <div className="min-w-37.5 border-l">
        <div
          className={
            category
              ? "sticky left-64 z-10 flex h-9 w-fit items-center px-4 text-sm"
              : "h-9"
          }
        >
          {category && (
            <>
              <div className="font-medium whitespace-nowrap">
                {category.title}
              </div>
              {canManage && (
                <CategoryModal
                  categoryId={category.id}
                  trigger={
                    <Button variant="ghost" size="icon">
                      <Info className="size-5 opacity-50" />
                    </Button>
                  }
                />
              )}
            </>
          )}
        </div>
        <div className="flex">
          {columns.map((columnMap, i) => (
            <ChartColumn
              key={i}
              columnIndex={i}
              tiers={tiers}
              documentRefsMap={documentRefsMap}
              containerRef={containerRef}
              columnMap={columnMap}
              setSelectedId={setSelectedId}
              canManage={canManage}
              onReorder={onReorder}
            />
          ))}
        </div>
        {!columns.length &&
          tiers.map((tier) => (
            <div
              key={tier.id}
              className="even:bg-background odd:bg-accent/70 flex h-44 items-center justify-center gap-2 px-4"
            />
          ))}
      </div>
    </ChartReorderContext.Provider>
  );
};

interface ChartColumnProps {
  columnIndex: number;
  tiers: TierDetails[];
  columnMap: ChartColumn;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  setSelectedId: (id: number) => void;
  canManage: boolean;
  onReorder: () => void;
}

const ChartColumn = ({
  columnIndex,
  tiers,
  columnMap,
  documentRefsMap,
  containerRef,
  setSelectedId,
  canManage,
  onReorder,
}: ChartColumnProps) => {
  const [hoveredId, setHoveredId] = useState<number | undefined>();

  return (
    <div className="flex w-full flex-col">
      {tiers.map((tier) => {
        const documents = columnMap.get(tier.id) ?? [];
        return (
          <TierCell
            key={tier.id}
            tierId={tier.id}
            columnIndex={columnIndex}
            documents={documents}
            documentRefsMap={documentRefsMap}
            containerRef={containerRef}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            setSelectedId={setSelectedId}
            canManage={canManage}
            onReorder={onReorder}
          />
        );
      })}
    </div>
  );
};

interface TierCellProps {
  tierId: number;
  columnIndex: number;
  documents: DocumentVersionSummary[];
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  hoveredId?: number;
  setHoveredId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedId: (id: number) => void;
  canManage: boolean;
  onReorder: () => void;
}

const TierCell = ({
  tierId,
  columnIndex,
  documents,
  documentRefsMap,
  containerRef,
  hoveredId,
  setHoveredId,
  setSelectedId,
  canManage,
  onReorder,
}: TierCellProps) => {
  const { t } = useTranslation();
  const { reorderKey, notifyReorderStart } = useContext(ChartReorderContext);
  const [localDocuments, setLocalDocuments] = useState(documents);
  const [prevDocumentsProp, setPrevDocumentsProp] = useState(documents);

  if (prevDocumentsProp !== documents) {
    setPrevDocumentsProp(documents);
    setLocalDocuments(documents);
  }

  const sortableType = `DOC_${tierId}_${columnIndex}`;

  const handleDragEnd = useCallback<OnDragEnd>(
    (event) => {
      const handle = async () => {
        if (event.canceled) return;
        if (!isSortableOperation(event.operation)) return;

        const source = event.operation.source;
        if (!source) return;

        const { initialIndex, index: targetIndex } = source;
        if (initialIndex === targetIndex) return;

        const reordered = moveItemByIndex(
          localDocuments,
          initialIndex,
          targetIndex,
        );
        setLocalDocuments(reordered);
        notifyReorderStart();

        const orderPayload = reordered.map((doc, i) => ({
          id: doc.document.id,
          chartOrder: i,
        }));

        try {
          await updateDocumentOrder(orderPayload);
          onReorder();
        } catch {
          toast.error(t(($) => $.toasts.error.reorderDocuments));
          setLocalDocuments(documents);
        }
      };

      void handle();
    },
    [documents, localDocuments, notifyReorderStart, onReorder, t],
  );

  const hasDraggableItems = canManage && localDocuments.length > 1;

  return (
    <div className="odd:bg-accent/70 even:bg-background flex h-44 items-center gap-2 px-4">
      {localDocuments.map((document) => (
        <DocumentLines
          key={`lines-${document.id}-${reorderKey}`}
          documentVersion={document}
          documentRefsMap={documentRefsMap}
          containerRef={containerRef}
          hoveredId={hoveredId}
          documents={documents}
        />
      ))}
      {hasDraggableItems ? (
        <DragDropProvider onDragEnd={handleDragEnd}>
          {localDocuments.map((document, index) => (
            <SortableDocumentCard
              key={document.id}
              index={index}
              sortableType={sortableType}
              documentVersion={document}
              documentRefsMap={documentRefsMap}
              hoveredId={hoveredId}
              setHoveredId={setHoveredId}
              setSelectedId={setSelectedId}
            />
          ))}
        </DragDropProvider>
      ) : (
        localDocuments.map((document) => (
          <DocumentCard
            key={document.id}
            documentVersion={document}
            documentRefsMap={documentRefsMap}
            hoveredId={hoveredId}
            setHoveredId={setHoveredId}
            setSelectedId={setSelectedId}
          />
        ))
      )}
    </div>
  );
};

interface SortableDocumentCardProps {
  index: number;
  sortableType: string;
  documentVersion: DocumentVersionSummary;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  hoveredId?: number;
  setHoveredId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedId: (id: number) => void;
}

const SortableDocumentCard = ({
  index,
  sortableType,
  documentVersion,
  documentRefsMap,
  hoveredId,
  setHoveredId,
  setSelectedId,
}: SortableDocumentCardProps) => {
  const { ref, handleRef, isDragging } = useSortable({
    id: `${documentVersion.document.id}`,
    index,
    type: sortableType,
    accept: sortableType,
  });

  return (
    <div ref={ref}>
      <DocumentCard
        documentVersion={documentVersion}
        documentRefsMap={documentRefsMap}
        hoveredId={hoveredId}
        setHoveredId={setHoveredId}
        setSelectedId={setSelectedId}
        isDragging={isDragging}
        handleRef={handleRef}
      />
    </div>
  );
};

export default ChartDocuments;
